// Version check (SPEC §9): GET /version.json every 10 minutes and on focus;
// if it differs from the running build, offer a reload.
import { useEffect, useState } from 'react';

export const VERSION_POLL_MS = 10 * 60 * 1000;

export async function fetchDeployedVersion(fetchFn: typeof fetch = (...a) => fetch(...a)): Promise<string | null> {
  try {
    const res = await fetchFn(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const json = (await res.json()) as { version?: unknown };
    return typeof json.version === 'string' ? json.version : null;
  } catch {
    return null; // offline or server down: say nothing
  }
}

export function useNewVersion(current: string, fetchFn?: typeof fetch, pollMs = VERSION_POLL_MS): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let stopped = false;
    const check = async () => {
      const v = await fetchDeployedVersion(fetchFn);
      if (!stopped && v && v !== current) setAvailable(true);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };
    const timer = setInterval(check, pollMs);
    addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [current, fetchFn, pollMs]);
  return available;
}
